import React, { useState } from 'react';
import Layout from '~/pages/templates/DefaultLayout';
import { asyncState, useAuth, useForm, useRouter, } from '~/hooks';
import { Card, LoadingSpinner } from '~/components';
import FormGroup from '~/components/form/form-group';
import TextArea from '~/components/form/form-control-textarea';
import { MdThumbUp } from 'react-icons/md';
import { useHistory } from 'react-router';
import { document, policySearchRequest, addressDetails } from '~/api';
import { stringUtils } from '~/util';
import { pickBy } from 'lodash';
import { PropertyAddressForm } from './PropertyAddressForm';
import { OwnerForm } from './OwnerForm';
import LegalInformationForm from './LegalInformationForm';
import { SectionHeader, PolicySearchRequestStatus } from './common';
import { DateTime } from 'luxon';

const defaultState = {
    readonly: false,
    fileNumber: '',
    ownerFirstName: '',
    ownerMiddleName: '',
    ownerLastName: '',
    ownerSuffix: '',
    streetNumber: '',
    streetName: '',
    streetSuffix: '',
    city: '',
    county: '',
    state: { label: '...', value: '' },
    stateOptions: [],
    zipcode: '',
    policyYear: '',
    deedYear: '',
    legalDescription: { label: 'Platted', value: 'platted' },
    legalDescriptionOptions: [
        { label: 'Platted', value: 'platted' },
        { label: 'Metes and Bounds', value: 'metesAndBounds' },
        { label: 'Not Provided', value: 'notProvided' }
    ],
    platBook: '',
    platPage: '',
    lot: '',
    subdivision: '',
    block: '',
    section: '',
    notes: '',
    metesAndBoundsDescription: '',
    files: [],
    loadingState: asyncState.SUCCESS
};

const onLoad = async (values, { requestId }) => {
    const stateOptions = addressDetails.getStateCodes();

    const nc = stateOptions.find(s => s.value == 'NC');

    if (requestId != null) {
        const request = await policySearchRequest.getById(requestId);

        const { _data } = request;

        const requestLocked = request.statusId == PolicySearchRequestStatus.InProgress;
        const requestCompleted = request.completed != null;

        const formState = {
            ...values,
            requestLocked,
            requestCompleted,
            readonly: true,
            fileNumber: _data.fileNumber || '',
            ownerFirstName: _data.ownerFirstName,
            ownerMiddleName: _data.ownerMiddleName || '',
            ownerLastName: _data.ownerLastName,
            ownerSuffix: _data.ownerSuffix || '',
            streetNumber: _data.streetNumber,
            streetName: _data.streetName,
            streetSuffix: _data.streetSuffix,
            city: _data.city,
            county: _data.county,
            zipcode: _data.zip || _data.zipcode,
            state: stateOptions.find(s => s.value == _data.state),
            policyYear: _data.policyYear,
            deedYear: _data.deedYear,
            legalDescription: defaultState.legalDescriptionOptions.find(l => l.value == _data.legalDescription),
            platBook: _data.platBook,
            platPage: _data.platPage,
            lot: _data.lot,
            subdivision: _data.subdivision,
            block: _data.block,
            section: _data.section,
            notes: _data.notes,
            metesAndBoundsDescription: _data.metesAndBoundsDescription
        };

        return {
            ...formState,
            stateOptions
        };
    }
    return {
        ...values,
        stateOptions,
        state: nc
    };
};

const validatePolicySearch = (data) => {
    if (!data.legalDescription.value) {
        return { legalDescription: 'Please select a Legal Description' };
    }

    const requiredFields = [
        'fileNumber',
        'streetNumber',
        'streetName',
        'zipcode',
        'city',
        'county'
    ];
    
    if (data.legalDescription.value == 'metesAndBounds') {
        requiredFields.push('metesAndBoundsDescription');
    }

    return requiredFields.reduce((acc, field) => {
        const value = data[field];
        if (!stringUtils.hasValue(value)) {
            acc[field] = 'This is a required field';
        } 
        return acc;
    }, {});

};

const onSubmit = async ({ values, props: { user }, onInvalidForm, resetFormErrors, onFail}) => {
    resetFormErrors();
    const errors = validatePolicySearch(values);

    if (Object.keys(errors).length > 0) {
        return onInvalidForm(errors);
    }

    const {
        state,
        legalDescription,
        files,
        ...otherData
    } = values;

    const localOnlyState = ['stateOptions', 'files'];

    const data = {
        ...pickBy(otherData, (_, key) => !localOnlyState.includes(key)),
        firmId: user.firmId,
        state: state.value,
        legalDescription: legalDescription.value,
        submitDate: DateTime.utc().toJSDate()
    };

    try {
        const searchRequest = await policySearchRequest.create(data);
        
        await Promise.all(
            files.map(async f => {
                await document.upload(f);
                const attachment = { ...f.uploadInfo, typeId: 1 };
                await policySearchRequest.addAttachment(searchRequest.id, attachment);
            })
        );

        await policySearchRequest.sendNewRequestEmail(searchRequest.id);
                
        return true;
    } catch (err) {
        console.error(err);
        return onFail(err.message);
    }
};

const notEmpty = val => stringUtils.hasValue(val) ? true : 'This is a required field';

const PolicySearchRequestPage = () => {
    const history = useHistory();
    const [status, setStatus] = useState(asyncState.IDLE);
    const { params } = useRouter();
    const { user } = useAuth();

    const {
        state,
        fieldActions,
        formActions
    } = useForm({
        initialState: defaultState,
        props: { user, requestId: params.id },
        onSubmit,
        onLoad,
        debug: true
    });

    const readonly = state.values.readonly;

    
    const setLoader = (loading = false) => {
        if (loading) {
            fieldActions.onChange(asyncState.PENDING, 'loadingState');
        } else {
            fieldActions.onChange(asyncState.SUCCESS, 'loadingState');
        }
    };

    return (
        <Layout title="Policy Search Request" subtitle="Submit a request to our team for a prior policy on your property">
            <div className="container h-full flex flex-col justify-center items-stretch relative">
                <Card className="p-6 mb-12 mt-12 flex-1 rounded-md flex flex-col">
                    {status == asyncState.SUCCESS && (
                        <div className="h-full w-full flex flex-col items-center justify-center">
                            <h1 className="text-4xl text-blue-60 font-bold mb-2">We&apos;re on it!</h1>
                            <p className="leading-8 text-lg">Thank you for your request! A member of the Tryon Title</p>
                            <p className="leading-8 text-lg">team will begin checking for a prior policy and will</p>
                            <p className="leading-8 text-lg">provide an update shortly.</p>
                            <button
                                className="rounded-full py-3 px-5 text-white bg-blue-50 my-4 focus:outline-none"
                                onClick={() => location.reload()}
                            >
                                Okay
                            </button>
                        </div>
                    )}
                    <LoadingSpinner status={status} />
                    <LoadingSpinner status={state.values.loadingState} />
                    {(status == asyncState.IDLE || status == asyncState.ERROR) &&
                        <React.Fragment>
                            {state.isErrored && (
                                <span className="bg-red-200 text-center text-red-500 text-lg block -mt-6 -mx-6 rounded-t-md px-6 py-2">
                                    There was an issue submitting your search request
                                </span>
                            )}
                            <div className="mt-8 grid grid-cols-1 lg:grid-cols-8 grid-flow-row auto-rows-auto gap-x-8 gap-y-4">
                                <FormGroup
                                    label="Your File No."
                                    required
                                    readOnly={readonly}
                                    groupClassName="col-span-4"
                                    path='fileNumber'
                                    value={state.values.fileNumber}
                                    error={state.errors.fileNumber}
                                    {...fieldActions}
                                    validator={notEmpty}
                                />
                                <SectionHeader colSpan={8}>Owner Name</SectionHeader>
                                <OwnerForm state={state} fieldActions={fieldActions} readonly={readonly} />

                                <SectionHeader colSpan={8}>Property Address</SectionHeader>
                                <PropertyAddressForm state={state} fieldActions={fieldActions} formActions={formActions} readonly={readonly} setLoader={setLoader} />

                                <SectionHeader colSpan={8}>Legal Information</SectionHeader>
                                <LegalInformationForm className='col-span-8' state={state} fieldActions={fieldActions} readonly={readonly} />
                                
                                <TextArea
                                    type="textarea"
                                    label="Notes"
                                    name="notes"
                                    path='notes'
                                    readOnly={readonly}
                                    groupClassName="col-span-8"
                                    {...fieldActions}
                                    value={state.values.notes}
                                    error={state.errors.notes}
                                />
                            </div>
                            {!readonly && (
                                <div className="flex justify-end items-center pt-8 mt-auto">
                                    <button
                                        className="py-3 px-5 ml-4 rounded-full bg-transparent border border-gray-300"
                                        onClick={() => history.push('/')}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className='py-3 px-5 ml-4 rounded-full text-white flex items-center focus:outline-none bg-lightGreen-300 disabled:opacity-50'
                                        disabled={Object.keys(state.errors).length}
                                        onClick={() => {
                                            if (Object.keys(state.errors).length < 1) {
                                                setStatus(asyncState.PENDING);
                                                
                                                formActions.submit((result) => {
                                                    if (result) {
                                                        setStatus(asyncState.SUCCESS);
                                                    } else {
                                                        setStatus(asyncState.ERROR);
                                                    }
                                                });
                                            }
                                        }}
                                    >
                                        Submit
                                        <MdThumbUp className="ml-2" size="1.25rem" />
                                    </button>
                                </div>
                            )}
                        </React.Fragment>}
                </Card>
            </div>
        </Layout>
    );
};

export default PolicySearchRequestPage;